import { Button, Spin } from "antd";
import { MaterialCiqIcon, SubmittalCiqIcon } from "components/svg-icons";
import { TOUR_TARGETS } from "components/tour";
import {
  FilterNamesEnum,
  GcDashBoardType,
  NavigationPageEnum
} from "../../models";
import { useContext, useMemo } from "react";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";

function NoPlanMessageBox({
  title,
  rowData
}: {
  title: string;
  rowData: {
    iconWithCount: React.ReactNode;
    text: string;
    navigateButton: React.ReactNode;
  }[];
}) {
  return (
    <div className="p-2 pb-6 flex flex-col space-y-2 w-full items-center box-border">
      <div className="box-header !text-sm w-full px-2">{title}</div>

      {rowData.map((row) => (
        <div
          className="flex w-full items-center px-2 py-1 space-x-4"
          key={row.text}
        >
          <div className="flex flex-wrap  min-w-[65px]">
            {row.iconWithCount}
          </div>
          <div className="text-xs grow">{row.text}</div>
          {row.navigateButton}
        </div>
      ))}
    </div>
  );
}

function PlanningDataQualityReport({
  dashboardData,
  goToFiltered
}: {
  dashboardData: GcDashBoardType | undefined;
  goToFiltered: (page: NavigationPageEnum, filter: FilterNamesEnum) => void;
}) {
  const { isProcoreProject }: TProjectContext = useContext(ProjectContext);

  const plannedData = useMemo(() => {
    if (!dashboardData) return null;

    const submittalsPlannedData = [];
    if (dashboardData?.submittals?.submittals_linked_to_deleted_activities) {
      submittalsPlannedData.push({
        iconWithCount: (
          <div className="flex items-center space-x-1 border border-solid border-ciq-primary-900-light px-2 py-0.5 bg-ciq-primary-900-light bg-opacity-30 rounded-sm text-xs">
            <SubmittalCiqIcon size={12} />
            <div className="max-w-xs min-w-[20px] text-xs">
              {
                dashboardData?.submittals
                  ?.submittals_linked_to_deleted_activities
              }
            </div>
          </div>
        ),
        text: "Linked Activities have been deleted. Relink to plan.",
        navigateButton: (
          <Button
            type="link"
            size="small"
            className="h-fit text-xs"
            onClick={() => {
              goToFiltered(
                NavigationPageEnum.SubmittalLog,
                FilterNamesEnum.LinkedToDeletedActivities
              );
            }}
            data-testid="index-button-create-plan-snpd"
          >
            View in Log
          </Button>
        )
      });
    }
    if (dashboardData?.submittals?.submittals_with_no_planned_dates) {
      submittalsPlannedData.push({
        iconWithCount: (
          <div className="flex items-center space-x-1 border border-solid border-ciq-primary-900-light px-2 py-0.5 bg-ciq-primary-900-light bg-opacity-30 rounded-sm text-xs">
            <SubmittalCiqIcon size={12} />
            <div className="max-w-xs min-w-[20px] text-xs">
              {dashboardData?.submittals?.submittals_with_no_planned_dates}
            </div>
          </div>
        ),
        text: "Link these to start planning and tracking.",
        navigateButton: (
          <Button
            type="link"
            size="small"
            className="h-fit text-xs"
            onClick={() => {
              goToFiltered(
                NavigationPageEnum.SubmittalLog,
                FilterNamesEnum.NoPlan
              );
            }}
            data-testid="index-button-create-plan-snpd"
          >
            View in Log
          </Button>
        )
      });
    }

    const materialPlannedData = [];
    if (dashboardData?.materials?.materials_linked_to_deleted_activities) {
      materialPlannedData.push({
        iconWithCount: (
          <div className="flex items-center space-x-1 border border-solid border-ciq-teal-400 px-2 py-0.5 bg-ciq-teal-400 bg-opacity-30 rounded-sm text-xs">
            <MaterialCiqIcon size={12} />
            <div className="max-w-xs min-w-[20px] text-xs">
              {dashboardData.materials.materials_linked_to_deleted_activities}
            </div>
          </div>
        ),
        text: "Linked Activities have been deleted. Relink to plan.",
        navigateButton: (
          <Button
            type="link"
            size="small"
            className="h-fit text-xs"
            onClick={() => {
              goToFiltered(
                NavigationPageEnum.MaterialLog,
                FilterNamesEnum.LinkedToDeletedActivities
              );
            }}
            data-testid="index-button-create-plan-mnpd"
          >
            View in log
          </Button>
        )
      });
    }

    if (dashboardData?.materials?.materials_with_no_planned_dates) {
      materialPlannedData.push({
        iconWithCount: (
          <div className="flex items-center space-x-1 border border-solid border-ciq-teal-400 px-2 py-0.5 bg-ciq-teal-400 bg-opacity-30 rounded-sm text-xs">
            <MaterialCiqIcon size={12} />
            <div className="max-w-xs min-w-[20px] text-xs">
              {dashboardData.materials.materials_with_no_planned_dates}
            </div>
          </div>
        ),
        text: "Link these to start planning and tracking.",
        navigateButton: (
          <Button
            type="link"
            size="small"
            className="h-fit text-xs"
            onClick={() => {
              goToFiltered(
                NavigationPageEnum.MaterialLog,
                FilterNamesEnum.NoPlan
              );
            }}
            data-testid="index-button-create-plan-mnpd"
          >
            View in log
          </Button>
        )
      });
    }

    const voidedSubmittalLinkedMaterialsCount =
      dashboardData?.materials?.materials_linked_with_voided_submittals;
    const deletedSubmittalLinkedMaterialsCount =
      dashboardData?.materials
        ?.materials_linked_with_deleted_from_source_submittals;

    if (!isProcoreProject && voidedSubmittalLinkedMaterialsCount) {
      materialPlannedData.push({
        iconWithCount: (
          <div className="flex items-center space-x-1 border border-solid border-ciq-teal-400 px-2 py-0.5 bg-ciq-teal-400 bg-opacity-30 rounded-sm text-xs">
            <MaterialCiqIcon size={12} />
            <div className="max-w-xs min-w-[20px] text-xs">
              {voidedSubmittalLinkedMaterialsCount}
            </div>
          </div>
        ),
        text: "Linked Submittals are voided. Relink to track.",
        navigateButton: (
          <Button
            type="link"
            size="small"
            className="h-fit text-xs"
            onClick={() => {
              goToFiltered(
                NavigationPageEnum.MaterialLog,
                FilterNamesEnum.IsLinkedVoidSubmittal
              );
            }}
            data-testid="index-button-create-plan-mlwvs"
          >
            View in log
          </Button>
        )
      });
    }

    if (isProcoreProject && deletedSubmittalLinkedMaterialsCount) {
      materialPlannedData.push({
        iconWithCount: (
          <div className="flex items-center space-x-1 border border-solid border-ciq-teal-400 px-2 py-0.5 bg-ciq-teal-400 bg-opacity-30 rounded-sm text-xs">
            <MaterialCiqIcon size={12} />
            <div className="max-w-xs min-w-[20px] text-xs">
              {deletedSubmittalLinkedMaterialsCount}
            </div>
          </div>
        ),
        text: "Linked Submittals are deleted. Relink to track.",
        navigateButton: (
          <Button
            type="link"
            size="small"
            className="h-fit text-xs"
            onClick={() => {
              goToFiltered(
                NavigationPageEnum.MaterialLog,
                FilterNamesEnum.IsLinkedDeletedSubmittal
              );
            }}
            data-testid="index-button-create-plan-mlwds"
          >
            View in log
          </Button>
        )
      });
    }

    return {
      submittals: submittalsPlannedData,
      materials: materialPlannedData
    };
  }, [dashboardData, goToFiltered, isProcoreProject]);

  return (
    <div className="box-border w-full" data-tour={TOUR_TARGETS.PLANNING_DUE}>
      <div className="box-header bottom-border !p-6">Planning Due</div>

      {plannedData ? (
        <div className="flex flex-col space-y-6 p-6">
          {plannedData.submittals.length === 0 &&
            plannedData.materials.length === 0 && (
              <div className="flex h-40 items-center p-2 justify-center text-xs">
                <div>
                  There are no submittals and materials which require planning.
                </div>
              </div>
            )}
          {plannedData.submittals.length > 0 ? (
            <NoPlanMessageBox
              title="Submittals"
              data-testid="no-plan-message-box-snpd"
              rowData={plannedData.submittals}
            />
          ) : null}

          {plannedData.materials.length > 0 ? (
            <NoPlanMessageBox
              title="Materials"
              data-testid="no-plan-message-box-mnpd"
              rowData={plannedData.materials}
            />
          ) : null}
        </div>
      ) : (
        <div className="flex h-40 items-center justify-center">
          <Spin />
        </div>
      )}
    </div>
  );
}

export default PlanningDataQualityReport;
